import { useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { HAK_CIPTA } from '../components/AppFooter';
import { LogoLensa } from '../components/LogoLensa';
import { ThemeToggle } from '../components/ThemeToggle';
import { LoginError, useAuth } from '../hooks/useAuth';
import { LanguageToggle } from '../components/LanguageToggle';
import { useBahasa } from '../i18n';

/**
 * Form login §F1: SATU field identifier berlabel "Username atau Email", plus
 * password dan checkbox "Ingat saya" (§F4.2).
 *
 * Pesan galat sengaja tidak dipilah — server memang selalu mengirim kalimat
 * yang sama untuk setiap jenis kegagalan (§F1 poin 4), dan UI tidak boleh
 * mengarang perbedaan yang tidak ada.
 *
 * Tata letak mengikuti paket `LENSA_LOGIN_FAMILY_v1.0` (brief Jawa, 16
 * September 2026, §G18): satu keluarga dengan VICON/HADIR. Desktop ≥861px =
 * hero kiri + satu kartu kanan; di bawahnya satu kolom — logo & tagline di
 * atas kartu, kota kecil SESUDAH form. Palet halaman ini punya token sendiri
 * (`--login-*`) yang tidak dipakai di luar sini.
 *
 * Ilustrasi kota adalah dua SVG statis (terang/gelap, viewBox 640×230) yang
 * ditukar CSS lewat kelas `dark` — mekanisme yang sama dengan `LogoLensa`.
 * Ukurannya ikut lebar kontainer dengan rasio intrinsik: tidak ada `cover`,
 * tinggi paksa, atau crop, jadi kamera dan gedungnya tidak pernah terpotong.
 */

/** Spek ikon rumah (lihat `components/IconAction.tsx`): 24 grid, stroke saja. */
const ikon = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const IkonOrang = () => (
  <svg {...ikon}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21v-2a8 8 0 0 1 16 0v2" />
  </svg>
);

const IkonGembok = () => (
  <svg {...ikon}>
    <rect x="5" y="10" width="14" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3m-4 5v2" />
  </svg>
);

const IkonMata = () => (
  <svg {...ikon}>
    <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IkonMataCoret = () => (
  <svg {...ikon}>
    <path d="m3 3 18 18M10.6 5.6a13 13 0 0 1 1.4-.1C18.4 5.5 22 12 22 12a22 22 0 0 1-3.3 3.9M6.6 6.6A23 23 0 0 0 2 12s3.6 6.5 10 6.5a12 12 0 0 0 5.4-1.4M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

const IkonPanah = () => (
  <svg {...ikon}>
    <path d="M4 12h15m-6-6 6 6-6 6" />
  </svg>
);

const IkonPerisai = () => (
  <svg {...ikon}>
    <path d="m12 3 8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z" />
    <path d="m8.5 11.5 2.5 2.5 4.5-5" />
  </svg>
);

/** Empat manfaat di hero. Uraiannya sengaja tanpa klaim enkripsi/"tanpa jeda". */
const MANFAAT: ReadonlyArray<{ judul: string; isi: string; gambar: ReactNode }> = [
  { judul: 'Secure', isi: 'Access based on user rights', gambar: <IkonPerisai /> },
  {
    judul: 'Real-time',
    isi: 'Live camera monitoring',
    gambar: (
      <svg {...ikon}>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="m10 9 5 3-5 3V9Z" />
      </svg>
    ),
  },
  {
    judul: 'Integrated',
    isi: 'Cameras in one view',
    gambar: (
      <svg {...ikon}>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    judul: 'Controlled',
    isi: 'Easier access management',
    gambar: (
      <svg {...ikon}>
        <path d="M15 21v-2a5 5 0 0 0-10 0v2m16 0v-2a5 5 0 0 0-4-4.9M16 3.1a4 4 0 0 1 0 7.8" />
        <circle cx="10" cy="7" r="4" />
      </svg>
    ),
  },
];

/** Kota terang/gelap ditukar CSS, bukan state — sama seperti `LogoLensa`. */
const Kota = () => (
  <>
    <img
      src="/images/login-kota-terang.svg"
      width={640}
      height={230}
      alt=""
      className="h-auto w-full dark:hidden"
    />
    <img
      src="/images/login-kota-gelap.svg"
      width={640}
      height={230}
      alt=""
      className="hidden h-auto w-full dark:block"
    />
  </>
);

/*
 * `svh` = tinggi viewport KECIL: yang tersisa saat bilah alamat browser ponsel
 * sedang terbentang. `100vh` di Safari iOS justru tinggi viewport BESAR, jadi
 * halaman setinggi `100vh` di sana dijamin melebihi layar sejak frame pertama.
 * Kelas `min-h-screen` tetap dipasang sebagai cadangan untuk browser lama.
 * Yang dikunci hanya tinggi MINIMUM — halaman tetap bisa digulung saat isinya
 * lebih tinggi (layar pendek, keyboard virtual, pesan galat panjang).
 */
const HALAMAN = {
  minHeight: '100svh',
  backgroundImage: 'radial-gradient(ellipse at 18% 55%, var(--login-ambient), transparent 54%)',
} as CSSProperties;

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useBahasa();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [lihatSandi, setLihatSandi] = useState(false);
  const [remember, setRemember] = useState(false);
  const [bantuan, setBantuan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const errorRef = useRef<HTMLParagraphElement>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(identifier, password, remember);
    } catch (err) {
      let message = (err as Error).message;
      if (err instanceof LoginError && err.retryAfter) {
        // Lewat t(): sebelumnya kalimat ini dirangkai dalam bahasa Inggris keras,
        // jadi UI berbahasa Indonesia memunculkan separuh kalimat asing.
        const menit = Math.ceil(err.retryAfter / 60);
        const lagi =
          menit === 1 ? t('Try again in 1 minute.') : t('Try again in {menit} minutes.', { menit });
        message = `${message} ${lagi}`;
      }
      setError(message);
      setPassword('');
      // Fokus dipindah ke pesan galat: pemakai screen reader tidak melihat
      // teks merah yang muncul di bawah tombol.
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setBusy(false);
    }
  }

  const kelasField =
    'block min-h-[50px] w-full rounded-[10px] border border-login-field bg-login-field-bg pl-[42px] pr-3 text-base text-login-heading placeholder:text-login-muted hover:border-login-muted focus:border-login-ink focus:outline-none focus:ring-[3px] focus:ring-login-soft sm:text-sm';
  const kelasLabel = 'mb-2 block text-xs font-semibold text-login-heading';
  const kelasIkonField =
    'pointer-events-none absolute inset-y-0 left-[14px] grid place-items-center text-login-muted';

  return (
    <div
      className="flex min-h-screen flex-col bg-login-bg text-sm text-login-heading"
      style={HALAMAN}
    >
      {/* — Bar atas: identitas induk Sandya + bahasa & tema, di semua lebar. — */}
      <header className="mx-auto flex w-full max-w-[1440px] items-center justify-between gap-3 px-5 py-3 sm:px-6 min-[861px]:min-h-[88px] min-[861px]:px-10 min-[861px]:py-4">
        <div className="flex min-w-0 items-center gap-5">
          <img
            src="/images/sandya.png"
            width={360}
            height={137}
            alt="Sandya Networks"
            className="w-[91px] shrink-0 sm:w-[104px] dark:brightness-0 dark:invert"
          />
          <span className="hidden border-l border-login-line pl-5 text-xs text-login-muted min-[861px]:inline">
            {t('Video Surveillance')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </header>

      <main className="flex w-full flex-1 items-center px-5 pb-3 pt-5 sm:px-6 min-[861px]:px-10 min-[861px]:pb-9 min-[861px]:pt-7">
        <div className="mx-auto w-full max-w-[440px] min-[861px]:grid min-[861px]:max-w-[1120px] min-[861px]:grid-cols-[minmax(0,1fr)_370px] min-[861px]:items-center min-[861px]:gap-11 min-[1101px]:grid-cols-[minmax(0,1fr)_minmax(360px,420px)] min-[1101px]:gap-[84px] min-[1600px]:max-w-[1220px] min-[1600px]:grid-cols-[minmax(0,1fr)_440px] min-[1600px]:gap-28">
          {/* — Hero, hanya ≥861px. Di bawah itu ia cuma mendorong form ke bawah lipatan. — */}
          <section className="hidden min-w-0 py-3 min-[861px]:block">
            <LogoLensa tinggi="h-auto w-[178px]" alt="Lensa" />
            <h2 className="mt-7 text-[37px] font-bold leading-[1.12] tracking-[-0.048em] min-[1101px]:text-[clamp(40px,3.65vw,51px)] min-[1600px]:text-[55px]">
              <span className="block">{t('Monitor. Protect.')}</span>
              <span className="block text-login-ink">{t('Trust.')}</span>
            </h2>
            <p className="mt-[18px] max-w-[405px] text-[13px] leading-[1.8] text-login-muted min-[1101px]:text-sm min-[1600px]:max-w-[432px] min-[1600px]:text-[15px]">
              {t(
                'An integrated video surveillance system for reliable, controlled security wherever you are.',
              )}
            </p>

            <ul className="mt-7 grid max-w-[500px] grid-cols-4 gap-2 min-[1101px]:gap-3">
              {MANFAAT.map((m) => (
                <li key={m.judul} className="min-w-0">
                  <span
                    aria-hidden="true"
                    className="mb-2 grid h-[33px] w-[33px] place-items-center rounded-[10px] border border-login-line bg-login-card text-login-ink"
                  >
                    {m.gambar}
                  </span>
                  <span className="block text-[11px] font-semibold">{t(m.judul)}</span>
                  <span className="mt-1 block max-w-[100px] text-[9px] leading-[1.6] text-login-muted min-[1101px]:text-[10px]">
                    {t(m.isi)}
                  </span>
                </li>
              ))}
            </ul>

            <figure aria-hidden="true" className="mt-5 w-full">
              <Kota />
            </figure>
            <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.13em] text-login-subtle">
              {t('One view. More peace of mind.')}
            </p>
          </section>

          <section className="w-full min-w-0">
            {/* Pengantar mobile: logo produk + tagline pendek, langsung disusul kartu. */}
            <div className="flex flex-col items-center gap-3 pb-6 text-center min-[861px]:hidden">
              <LogoLensa tinggi="h-auto w-[133px] sm:w-36" alt="Lensa" />
              <p className="text-[11px] text-login-muted sm:text-xs">{t('Monitor. Protect. Trust.')}</p>
            </div>

            <form
              onSubmit={onSubmit}
              aria-busy={busy}
              className="rounded-[22px] border border-login-line bg-login-card p-5 shadow-[var(--login-shadow)] sm:p-6 min-[861px]:p-7 min-[1101px]:p-[30px]"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h1 className="text-[25px] font-bold leading-[1.3] tracking-[-0.04em] sm:text-[27px]">
                    {t('Sign in')}
                  </h1>
                  <p className="mt-1.5 text-xs leading-[1.7] text-login-muted">
                    {t('Sign in with your LENSA account.')}
                  </p>
                </div>
                <span
                  aria-hidden="true"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-login-soft text-login-ink sm:h-10 sm:w-10"
                >
                  <IkonGembok />
                </span>
              </div>

              <div className="mt-6">
                <label htmlFor="identifier" className={kelasLabel}>
                  {t('Username or email')}
                </label>
                <div className="relative">
                  <span aria-hidden="true" className={kelasIkonField}>
                    <IkonOrang />
                  </span>
                  <input
                    id="identifier"
                    name="identifier"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    autoFocus
                    required
                    placeholder={t('Enter your username or email')}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? 'login-error' : undefined}
                    className={kelasField}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="password" className={kelasLabel}>
                  {t('Password')}
                </label>
                <div className="relative">
                  <span aria-hidden="true" className={kelasIkonField}>
                    <IkonGembok />
                  </span>
                  <input
                    id="password"
                    name="password"
                    type={lihatSandi ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    placeholder={t('Enter your password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? 'login-error' : undefined}
                    className={`${kelasField} pr-12`}
                  />
                  {/* Label tombol menyebut apa yang AKAN terjadi — beda dari toggle
                      tema & bahasa, yang menyebut keadaan sekarang. Di sana yang
                      ditanya "sekarang apa"; di sini isi field sudah terlihat
                      sendiri, yang tidak terlihat justru akibat menekannya. */}
                  <button
                    type="button"
                    onClick={() => setLihatSandi((v) => !v)}
                    aria-pressed={lihatSandi}
                    aria-controls="password"
                    aria-label={lihatSandi ? t('Hide password') : t('Show password')}
                    title={lihatSandi ? t('Hide password') : t('Show password')}
                    className="absolute inset-y-[3px] right-[3px] grid w-11 place-items-center rounded-lg text-login-muted transition-colors hover:bg-login-field-hover hover:text-login-ink"
                  >
                    {lihatSandi ? <IkonMataCoret /> : <IkonMata />}
                  </button>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3">
                <label className="flex min-h-tap cursor-pointer items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-[17px] w-[17px] rounded accent-[var(--login-accent)]"
                  />
                  {t('Keep me signed in')}
                </label>
                {/* Bantuan = disclosure teks umum ke administrator. Tidak ada alur
                    reset password mandiri di Lensa, jadi tidak ada tautan ke sana. */}
                <button
                  type="button"
                  onClick={() => setBantuan((v) => !v)}
                  aria-expanded={bantuan}
                  aria-controls="login-bantuan"
                  className="min-h-tap text-[11px] font-medium text-login-ink hover:underline"
                >
                  {t('Need help?')}
                </button>
              </div>
              {bantuan && (
                <div
                  id="login-bantuan"
                  className="mt-1 rounded-[10px] border border-login-line bg-login-field-bg px-3.5 py-3 text-xs leading-[1.7] text-login-muted"
                >
                  <strong className="block font-semibold text-login-heading">
                    {t('Trouble accessing your account?')}
                  </strong>
                  {t(
                    'Contact the LENSA administrator in your organization for account help or a password reset.',
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="mt-4 flex min-h-[50px] w-full items-center justify-between gap-3 rounded-[10px] bg-login px-4 text-[13px] font-semibold text-login-contrast shadow-[var(--login-shadow-accent)] transition-colors hover:bg-login-hover disabled:opacity-80"
              >
                {busy ? t('Signing in…') : t('Sign in')}
                {busy ? (
                  <span
                    aria-hidden="true"
                    className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-white/35 border-t-white motion-reduce:animate-none"
                  />
                ) : (
                  <IkonPanah />
                )}
              </button>

              {error && (
                <p
                  id="login-error"
                  ref={errorRef}
                  tabIndex={-1}
                  role="alert"
                  className="mt-4 rounded-[10px] bg-login-danger-bg px-3.5 py-3 text-xs leading-[1.7] text-login-danger [overflow-wrap:anywhere]"
                >
                  {error}
                </p>
              )}

              <div className="mt-6 flex items-start gap-3 border-t border-login-line pt-5">
                <span aria-hidden="true" className="mt-0.5 shrink-0 text-login-ink">
                  <IkonPerisai />
                </span>
                <p className="text-[10px] leading-[1.7] text-login-muted">
                  <strong className="block text-[11px] font-medium text-login-heading">
                    {t('Access based on your permissions')}
                  </strong>
                  {t('Choose “Keep me signed in” only on a personal device.')}
                </p>
              </div>
            </form>

            {/* Kota kecil SESUDAH form dalam alur normal, bukan latar di belakang
                isian. Di layar pendek ia disembunyikan, bukan dipotong. */}
            <figure
              aria-hidden="true"
              className="mx-auto mt-3 w-[280px] max-w-full sm:w-[360px] min-[861px]:hidden [@media(max-height:650px)]:hidden"
            >
              <Kota />
            </figure>
          </section>
        </div>
      </main>

      {/* Hak cipta ikut alur dokumen (§G15) — tidak pernah menutupi kartu. */}
      <footer className="mx-auto flex w-full max-w-[1360px] flex-col items-center gap-1 px-6 pb-[max(21px,env(safe-area-inset-bottom))] pt-2 text-[10px] leading-[1.6] text-login-subtle min-[861px]:flex-row min-[861px]:justify-between min-[861px]:px-10 min-[861px]:pb-[max(22px,env(safe-area-inset-bottom))] min-[861px]:pt-[18px]">
        <p>{HAK_CIPTA}</p>
        <p className="tracking-[0.025em]">{t('Secure • Reliable • Integrated')}</p>
      </footer>
    </div>
  );
}
